/**
 * Pure date input conversion utilities (SRP).
 * No dependencies on i18n or globals.
 */

export const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
export const ISO_DATE_TIME_REGEX = /^\d{4}-\d{2}-\d{2}(T|$)/;

export type DateSourceFormat = 'display' | 'entrata';

export function normalizeToIsoDate(value: string): string {
  if (ISO_DATE_REGEX.test(value)) return value;
  if (ISO_DATE_TIME_REGEX.test(value)) return value.slice(0, 10);
  return value;
}

function getDateSeparator(pipeFormat: string | undefined): string {
  return pipeFormat?.match(/[^dmy]/i)?.[0] ?? '/';
}

function getDateFormatToken(partType: Intl.DateTimeFormatPartTypes): string {
  if (partType === 'year') return 'yyyy';
  if (partType === 'month') return 'mm';
  return 'dd';
}

/**
 * Derives the numeric date-input pattern from the selected locale when the
 * i18n package has no date-input configuration for that locale.
 */
export function getLocaleDateInputPipeFormat(locale: string): string {
  try {
    const parts = new Intl.DateTimeFormat(locale.replace(/_/g, '-'), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date(2006, 10, 9));
    const separator = parts.find((part) => part.type === 'literal')?.value ?? '/';
    const dateParts = parts.filter(
      (part) => part.type === 'year' || part.type === 'month' || part.type === 'day',
    );
    if (dateParts.length !== 3) return 'mm/dd/yyyy';

    return dateParts.map((part) => getDateFormatToken(part.type)).join(separator);
  } catch {
    return 'mm/dd/yyyy';
  }
}

/**
 * Converts ISO date (YYYY-MM-DD) to locale display format.
 * pipeFormat: dd/mm/yyyy | dd.mm.yyyy | mm/dd/yyyy | yyyy-mm-dd
 */
export function isoToDisplay(isoDate: string, pipeFormat: string | undefined): string {
  const datePart = isoDate?.slice(0, 10);
  if (!isoDate || !ISO_DATE_REGEX.test(datePart)) {
    return isoDate ?? '';
  }
  const [y, m, d] = datePart.split('-');
  const sep = getDateSeparator(pipeFormat);
  const fmt = (pipeFormat ?? '').toLowerCase();
  if (fmt.startsWith('yyyy')) {
    return `${y}${sep}${m}${sep}${d}`;
  }
  if (fmt.startsWith('dd')) {
    return `${d}${sep}${m}${sep}${y}`;
  }
  return `${m}${sep}${d}${sep}${y}`;
}

/**
 * Formats an ISO date using the browser's locale conventions while preserving
 * the calendar date. This is used when @entrata/i18n has no date-input
 * configuration for a locale (for example de-DE).
 */
export function formatIsoDateWithIntl(isoDate: string, locale: string): string {
  const datePart = isoDate?.slice(0, 10);
  if (!isoDate || !ISO_DATE_REGEX.test(datePart)) return isoDate ?? '';

  const [year, month, day] = datePart.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const intlLocale = locale.replace(/_/g, '-');

  try {
    return new Intl.DateTimeFormat(intlLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  } catch {
    return isoToDisplay(datePart, 'mm/dd/yyyy');
  }
}

/**
 * Fallback parser when i18n is unavailable. Accepts ISO or mm/dd/yyyy (US default).
 */
export function fallbackParseToIso(value: string): string {
  if (ISO_DATE_REGEX.test(value)) return value;
  const match = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!match) return value;
  const [, month, day, year] = match;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

/** Parses a numeric display date according to its locale-specific pattern. */
export function parseLocaleDateInputToIso(value: string, pipeFormat: string | undefined): string {
  if (ISO_DATE_REGEX.test(value)) return value;

  const tokens = (pipeFormat ?? 'mm/dd/yyyy').toLowerCase().match(/yyyy|mm|dd/g);
  const values = value.trim().split(/\D+/).filter(Boolean);
  if (!tokens || tokens.length !== 3 || values.length !== 3) return value;

  const parts = Object.fromEntries(tokens.map((token, index) => [token, values[index]]));
  const year = parts.yyyy;
  const month = parts.mm;
  const day = parts.dd;
  if (!year || !month || !day || year.length !== 4) return value;

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

export function isIsoFormatted(value: string): boolean {
  return ISO_DATE_REGEX.test(value) || ISO_DATE_TIME_REGEX.test(value);
}

function isValidIsoDate(value: string): boolean {
  if (!ISO_DATE_REGEX.test(value)) return false;

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

/**
 * Normalizes application state date strings to ISO (YYYY-MM-DD) when possible.
 * Handles ISO values, Entrata US mm/dd/yyyy payloads, and locale-aware display parsing.
 */
export function resolveIsoDateValue(
  value: string,
  parseDisplayToIso?: (display: string) => string,
  sourceFormat: DateSourceFormat = 'display',
): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return '';

  const isoCandidate = trimmed.slice(0, 10);
  if (isIsoFormatted(isoCandidate) && isValidIsoDate(isoCandidate)) return isoCandidate;

  const usDateMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const parseUsDate = (): string => {
    if (!usDateMatch) return '';

    const [, month, day, year] = usDateMatch;
    const candidate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    return isValidIsoDate(candidate) ? candidate : '';
  };

  // Entrata's slash-formatted payloads are always MM/DD/YYYY. Resolve them
  // before consulting the display-locale parser, where ambiguous values such
  // as 04/01/2024 could otherwise become 1 April or 4 January incorrectly.
  if (sourceFormat === 'entrata') {
    const parsedUsDate = parseUsDate();
    if (parsedUsDate) return parsedUsDate;
  }

  if (parseDisplayToIso) {
    const parsed = parseDisplayToIso(trimmed);
    const parsedDate = parsed.slice(0, 10);
    if (isIsoFormatted(parsedDate) && isValidIsoDate(parsedDate)) return parsedDate;
  }

  return parseUsDate();
}

/**
 * Formats date input as the user types, inserting separators automatically.
 * pipeFormat: dd/mm/yyyy | dd.mm.yyyy | mm/dd/yyyy | yyyy-mm-dd
 */
export function formatDateInputAsYouType(value: string, pipeFormat: string | undefined): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length === 0) return '';

  const sep = getDateSeparator(pipeFormat);
  const fmt = (pipeFormat ?? 'mm/dd/yyyy').toLowerCase();

  if (fmt.startsWith('yyyy')) {
    if (digits.length <= 4) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 4)}${sep}${digits.slice(4)}`;
    return `${digits.slice(0, 4)}${sep}${digits.slice(4, 6)}${sep}${digits.slice(6)}`;
  }

  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}${sep}${digits.slice(2)}`;
  return `${digits.slice(0, 2)}${sep}${digits.slice(2, 4)}${sep}${digits.slice(4)}`;
}
